import { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Chart, Line, Bar } from 'react-chartjs-2';
import { CalculationResults } from '../../types';
import { formatValue } from '../../utils';
import { useApp } from '../../contexts/AppContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AnalyzerChartsProps {
  calculations: CalculationResults;
}

const AnalyzerCharts: React.FC<AnalyzerChartsProps> = ({ calculations }) => {
  const { projectionDurationYears } = useApp();
  const chartRefs = useRef<{ [key: string]: ChartJS | null }>({});

  // Cleanup chart instances on unmount
  useEffect(() => {
    // Capture refs for cleanup
    const charts = chartRefs.current;
    
    return () => {
      Object.values(charts).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);

  if (!calculations || !calculations.projectionData) {
    return null;
  }

  const filteredData = calculations.projectionData.filter(
    d => d.jahr > 0 && d.jahr <= projectionDurationYears
  );

  const chartColors = {
    blue: '#3B82F6',
    green: '#10B981',
    red: '#EF4444',
    lightBlue: 'rgba(59, 130, 246, 0.2)',
    lightGreen: 'rgba(16, 185, 129, 0.2)',
    lightRed: 'rgba(239, 68, 68, 0.2)',
  };

  // Cashflow Chart Data
  const cashflowChartData = {
    labels: filteredData.map(d => d.jahr),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Monatl. Cashflow vor Steuern',
        data: filteredData.map(d => d.cashflowVorSteuer / 12),
        backgroundColor: filteredData.map(d => 
          d.cashflowVorSteuer < 0 ? chartColors.lightRed : chartColors.lightBlue
        ),
        borderColor: filteredData.map(d => 
          d.cashflowVorSteuer < 0 ? chartColors.red : chartColors.blue
        ),
        borderWidth: 1,
      },
      {
        type: 'line' as const,
        label: 'Monatl. Cashflow nach Steuern',
        data: filteredData.map(d => d.cashflow / 12),
        borderColor: chartColors.green,
        backgroundColor: 'transparent',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: chartColors.green,
      }
    ]
  };

  // Debt vs Value Chart Data
  const debtValueChartData = {
    labels: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.jahr),
    datasets: [
      {
        label: 'Immobilienwert',
        data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.immobilienwert),
        borderColor: chartColors.blue,
        backgroundColor: chartColors.lightBlue,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Restschuld',
        data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.restschuld),
        borderColor: chartColors.red,
        backgroundColor: chartColors.lightRed,
        fill: true,
        tension: 0.4,
      }
    ]
  };

  // Wealth Chart Data
  const wealthChartData = {
    labels: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.jahr),
    datasets: [
      {
        label: 'Eigentumsanteil',
        data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.eigentumsanteil),
        borderColor: chartColors.blue,
        borderWidth: 2,
        borderDash: [5, 5],
        tension: 0.4,
        fill: false,
        yAxisID: 'y',
      },
      {
        label: 'Nettovermögen',
        data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.vermoegen),
        borderColor: chartColors.green,
        backgroundColor: chartColors.lightGreen,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        yAxisID: 'y',
      },
      {
        label: 'Nettovermögen / EK',
        data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => 
          calculations.eigenkapitalSoll > 0 ? (d.vermoegen / calculations.eigenkapitalSoll) * 100 : 0
        ),
        borderColor: '#8B5CF6',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        yAxisID: 'y1',
      }
    ]
  };

  // Cumulative Cashflow Chart Data
  const cumulativeCashflowData = {
    labels: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.jahr),
    datasets: [{
      label: 'Kumulierter Cashflow',
      data: calculations.projectionData.slice(0, projectionDurationYears + 1).map(d => d.cumulativeCashflowNachSteuer || 0),
      borderColor: chartColors.green,
      backgroundColor: chartColors.lightGreen,
      borderWidth: 2,
      fill: true,
      tension: 0.4,
    }]
  };

  // Zinsen & Tilgung Chart Data
  const zinsenTilgungData = {
    labels: filteredData.map(d => d.jahr),
    datasets: [
      {
        label: 'Zinsen',
        data: filteredData.map(d => d.zinsenJahr || 0),
        backgroundColor: 'rgba(255, 182, 193, 0.8)', // Light pink
        borderColor: 'rgb(255, 105, 180)',
        borderWidth: 1,
      },
      {
        label: 'Tilgung',
        data: filteredData.map(d => d.tilgungJahr || 0),
        backgroundColor: 'rgba(176, 224, 230, 0.8)', // Light cyan
        borderColor: 'rgb(0, 206, 209)',
        borderWidth: 1,
      }
    ]
  };

  // Kumulierter Zinsaufwand Chart Data
  const kumulierterZinsaufwandData = {
    labels: filteredData.map(d => d.jahr),
    datasets: [{
      label: 'Kumulierter Zinsaufwand',
      data: filteredData.map(d => d.cumulativeZinsen || 0),
      backgroundColor: 'rgba(255, 223, 186, 0.8)', // Light peach
      borderColor: 'rgb(255, 165, 0)',
      borderWidth: 2,
      fill: true,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatValue(context.parsed.y, '€');
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Jahr'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Betrag (€)'
        },
        ticks: {
          callback: (value: any) => formatValue(value, '€')
        }
      }
    }
  };

  const wealthChartOptions = {
    ...chartOptions,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Jahr'
        }
      },
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Betrag (€)'
        },
        ticks: {
          callback: (value: any) => formatValue(value)
        }
      },
      y1: {
        type: 'linear' as const,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Rendite auf EK (%)'
        },
        grid: {
          drawOnChartArea: false
        },
        ticks: {
          callback: (value: any) => formatValue(value, '%')
        }
      }
    }
  };

  return (
    <div className="mt-8 space-y-8">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Cashflow Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Cashflow-Entwicklung</h3>
          <div className="flex items-center gap-4 mb-2 text-sm text-gray-600">
            <span>Monatl. CF für Jahr: <strong className="text-red-600">-{formatValue(Math.abs(calculations.monatlicherCashflowJahr1IstVorSteuer), '€')}</strong></span>
            <span>Vor St.</span>
            <span>Nach St. <strong className="text-red-600">-{formatValue(Math.abs(calculations.monatlicherCashflowJahr1IstNachSteuer), '€')}</strong></span>
          </div>
          <div style={{ height: '400px' }}>
            <Chart 
              type='bar' 
              data={cashflowChartData} 
              options={chartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['cashflow'] = ref as unknown as ChartJS<'bar'>;
                }
              }}
            />
          </div>
        </div>

        {/* Wealth Chart - renamed to match screenshot */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Vermögensaufbau ({projectionDurationYears} Jahre)</h3>
          <div className="text-sm text-gray-600 mb-2">
            Hinweis: Nettovermögen = Eigentumsanteil - EK-Bedarf
          </div>
          <div style={{ height: '400px' }}>
            <Line 
              data={wealthChartData} 
              options={wealthChartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['wealth'] = ref as unknown as ChartJS<'line'>;
                }
              }}
            />
          </div>
        </div>

        {/* Zinsen & Tilgung Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Zinsen & Tilgung pro Jahr ({projectionDurationYears} Jahre)</h3>
          <div style={{ height: '400px' }}>
            <Bar 
              data={zinsenTilgungData}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['zinsen'] = ref as unknown as ChartJS<'bar'>;
                }
              }}
              options={{
              ...chartOptions,
              scales: {
                ...chartOptions.scales,
                x: {
                  stacked: true,
                  title: {
                    display: true,
                    text: 'Jahr'
                  }
                },
                y: {
                  stacked: true,
                  title: {
                    display: true,
                    text: 'Betrag (€)'
                  },
                  ticks: {
                    callback: (value: any) => formatValue(value, '€')
                  }
                }
              }
            }} />
          </div>
        </div>

        {/* Kumulierter Zinsaufwand Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Kumulierter Zinsaufwand ({projectionDurationYears} Jahre)</h3>
          <div style={{ height: '400px' }}>
            <Bar 
              data={kumulierterZinsaufwandData} 
              options={chartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['zinsaufwand'] = ref as unknown as ChartJS<'bar'>;
                }
              }}
            />
          </div>
        </div>

        {/* Debt vs Value Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Kreditverlauf & Immobilienwert ({projectionDurationYears} Jahre)</h3>
          <div style={{ height: '400px' }}>
            <Line 
              data={debtValueChartData} 
              options={chartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['debtValue'] = ref as unknown as ChartJS<'line'>;
                }
              }}
            />
          </div>
        </div>

        {/* Cumulative Cashflow Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Kumulierter Cashflow nach Steuern ({projectionDurationYears} Jahre)</h3>
          <div style={{ height: '400px' }}>
            <Line 
              data={cumulativeCashflowData} 
              options={chartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['cumulative'] = ref as unknown as ChartJS<'line'>;
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyzerCharts;